import React from 'react';
import { IconButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
// import Checkbox from '@material-ui/core/Checkbox';
import { useOnMount, useActionReducer } from 'utils/react';
import { MTable } from 'ui/mtable/MTable';
import { MappingDialog } from './MappingDialog';

import formatters from '../../../../../formatters';

import { CompanyStatus } from './Components';
import ConfirmationDialog from 'ui/dialog/ConfirmationDialog';

const FIELD_COMPANY = 'external_company';
const FIELD_AGREEMENT = 'external_agreement';

const Dialogs = {
    MAPPING: 'mapping',
    CONFIRM: 'confirm',
};

function getColumns(lookups, config, localization, actions, editable) {
    const columns = [
        {
            field: 'company_uuid',
            title: 'Uuid',
            notFilter: true,
            hidden: true,
            export: false,
        },
        {
            field: 'company_name',
            title: 'CyberFOX Company',
            defaultSort: 'desc',
            readonly: true,
            editable: 'never',
        },
        {
            field: FIELD_COMPANY,
            title: `${config.label} Company`,
            lookup: lookups[FIELD_COMPANY],
        },
        {
            field: FIELD_AGREEMENT,
            title: localization.companyMappingAgreementColumn(config),
            lookup: lookups[FIELD_AGREEMENT],
        },
        {
            field: 'last_sync',
            title: 'Last sync',
            readonly: true,
            editable: 'never',
            render: row => formatters.date(row.last_sync),
        },
        {
            field: 'status',
            title: 'Status',
            readonly: true,
            editable: 'never',
            render: row => (
                <CompanyStatus status={row.status} message={row.message} />
            ),
        },
    ];

    if (editable) {
        columns.push({
            field: 'action',
            title: '',
            notFilter: true,
            export: false,
            cellStyle: {
                width: '130px',
            },
            render: row => {
                return (
                    <>
                        <IconButton
                            color="primary"
                            onClick={() => actions.onEdit(row)}
                        >
                            <EditIcon />
                        </IconButton>
                        <IconButton
                            color="primary"
                            onClick={() => actions.onDelete(row)}
                            disabled={
                                !row.external_company && !row.external_agreement
                            }
                        >
                            <DeleteIcon />
                        </IconButton>
                    </>
                );
            },
        });
    }

    return columns;
}

const Actions = {
    LOADING: 'change',
    LOADED: 'loaded',
    UPDATE: 'update',
    UPDATE_LOOKUPS: 'update.lookups',
    DIALOG: 'dialog',
    CLOSE_DIALOG: 'close_dialog',
};

const initState = {
    loading: false,
    loaded: false,
    data: [],
    lookups: {},
    selected: [],
    dialog: {},
};
const getSelected = data =>
    data.filter(row => row[FIELD_COMPANY]).map(row => row[FIELD_COMPANY]);

const reducer = config => (state, { action, payload }) => {
    switch (action) {
        case Actions.DIALOG: {
            return { ...state, dialog: payload };
        }
        case Actions.CLOSE_DIALOG: {
            return { ...state, dialog: {} };
        }
        case Actions.LOADING: {
            return { ...state, loading: true };
        }
        case Actions.LOADED: {
            const { data, lookups } = payload;
            return {
                loading: false,
                loaded: true,
                data,
                lookups,
                selected: getSelected(data),
            };
        }
        case Actions.UPDATE: {
            const { data } = payload;
            return {
                ...state,
                data,
                selected: getSelected(data),
            };
        }
        case Actions.UPDATE_LOOKUPS: {
            let { lookups } = payload;
            lookups = Object.keys(lookups).reduce((acc, field) => {
                acc[field] = { ...acc[field], ...lookups[field] };
                return acc;
            }, state.lookups);
            return { ...state, lookups };
        }
        default:
            throw Error(`Not recognize: ${action}`);
    }
};

export default ({ context: { service, config, localization, editable } }) => {
    const [state, dispatch] = useActionReducer(reducer(config), initState);
    const { serviceProvider } = config;

    useOnMount(async () => {
        dispatch(Actions.LOADING);
        const { data, lookups } = await serviceProvider.companies(
            service,
            config
        );
        dispatch(Actions.LOADED, { data, lookups });
    });
    const update = async data => {
        dispatch(Actions.UPDATE, { data });
        await serviceProvider.updateCompanies(service, data, state.lookups);
    };

    const onMappingSave = async (values, rowData) => {
        const { company_uuid, company_name } = rowData;
        const external_company = values.company.value;
        const external_agreement = values.agreement.value;

        if (!external_company || !external_agreement) {
            dispatch(Actions.CLOSE_DIALOG);
            return;
        }
        const newData = state.data.map(row => {
            if (row.company_uuid === company_uuid) {
                return {
                    company_uuid,
                    company_name,
                    external_company,
                    external_agreement,
                    status: 'waiting',
                };
            }
            return row;
        });
        const lookups = {
            external_company: {
                [external_company]: values.company.label,
            },
            external_agreement: {
                [external_agreement]: values.agreement.label,
            },
        };
        dispatch(Actions.UPDATE_LOOKUPS, { lookups });
        await update(newData);
        dispatch(Actions.CLOSE_DIALOG);
    };

    const onDelete = async ({ company_uuid, company_name }) => {
        const newData = state.data.map(row => {
            if (row.company_uuid === company_uuid) {
                return {
                    company_uuid,
                    company_name,
                    external_company: '',
                    external_agreement: '',
                };
            }
            return row;
        });
        update(newData);
        dispatch(Actions.CLOSE_DIALOG);
    };

    const actions = {
        onEdit: row =>
            dispatch(Actions.DIALOG, {
                type: Dialogs.MAPPING,
                payload: row,
            }),
        onDelete: row =>
            dispatch(Actions.DIALOG, {
                type: Dialogs.CONFIRM,
                payload: row,
            }),
    };
    const columns = getColumns(
        state.lookups,
        config,
        localization,
        actions,
        editable
    );

    const dialogType = state.dialog && state.dialog.type;
    return (
        <>
            {dialogType === Dialogs.MAPPING && (
                <MappingDialog
                    rowData={state.dialog.payload}
                    lookups={state.lookups}
                    config={config}
                    service={service}
                    onCancel={() => dispatch(Actions.CLOSE_DIALOG)}
                    onSave={onMappingSave}
                />
            )}
            {dialogType === Dialogs.CONFIRM && (
                <ConfirmationDialog
                    open
                    onOk={() => onDelete(state.dialog.payload)}
                    onCancel={() => dispatch(Actions.CLOSE_DIALOG)}
                    title="Are you sure you want remove this company mapping?"
                />
            )}
            <MTable
                loading={state.loading}
                columns={columns}
                data={state.data}
            />
        </>
    );
};
